import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Tag, Check, X, Bot, ExternalLink, Loader2 } from "lucide-react";

function Candidate({ c, reload }) {
  const [title, setTitle] = useState(c.title);
  const [discount, setDiscount] = useState(c.discount || "");
  const [description, setDescription] = useState(c.description || "");
  const [validUntil, setValidUntil] = useState(c.valid_until || "");
  const [busy, setBusy] = useState(false);
  const publish = async () => {
    setBusy(true);
    try { await api.post(`/admin/deal-candidates/${c.id}/publish`, { title, discount, description, valid_until: validUntil }); toast.success("Deal is live"); reload(); }
    catch { toast.error("Publish failed"); setBusy(false); }
  };
  const dismiss = async () => { await api.delete(`/admin/deal-candidates/${c.id}`); toast("Dismissed"); reload(); };
  return (
    <div className="border border-white/10 bg-[#141414] p-3 space-y-2" data-testid={`deal-candidate-${c.id}`}>
      <div className="flex items-center gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`deal-title-${c.id}`} placeholder="Title" className="flex-1 bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#00E5FF]" />
        <input value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid={`deal-discount-${c.id}`} placeholder="Discount" className="w-40 bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-[#FFEA00] focus:outline-none focus:border-[#00E5FF]" />
      </div>
      <div className="flex items-center gap-2">
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" className="flex-1 bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-neutral-300 focus:outline-none focus:border-[#00E5FF]" />
        <input value={validUntil} onChange={(e) => setValidUntil(e.target.value)} placeholder="Valid until" className="w-32 bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-sm text-white" />
        {c.source_url && <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-white" title="Source"><ExternalLink size={15} /></a>}
        <button onClick={publish} disabled={busy} data-testid={`deal-publish-${c.id}`} className="bg-[#00FF66] text-black text-xs font-bold px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"><Check size={13} /> Approve</button>
        <button onClick={dismiss} data-testid={`deal-dismiss-${c.id}`} className="text-[#FF0055] hover:text-white px-2"><X size={16} /></button>
      </div>
    </div>
  );
}

export default function DealsReview() {
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => { setLoading(true); api.get("/admin/deal-candidates").then((r) => setCands(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/admin/venues/harvest-deals/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const harvest = async () => {
    setRunning(true);
    try {
      await api.post("/admin/venues/harvest-deals");
      toast.success("Deal harvester started — check back in a minute");
      const poll = setInterval(async () => {
        const { data } = await api.get("/admin/venues/harvest-deals/status");
        setStatus(data);
        if (data.state !== "running") { clearInterval(poll); setRunning(false); load(); }
      }, 5000);
    } catch { toast.error("Failed to start"); setRunning(false); }
  };

  const groups = cands.reduce((a, c) => { (a[c.venue_name] = a[c.venue_name] || []).push(c); return a; }, {});

  return (
    <div data-testid="deals-review-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl text-white flex items-center gap-2"><Tag size={22} className="text-[#FFEA00]" /> Deals Review</h2>
          <p className="text-neutral-500 text-sm mt-1">The RiNo Ranger found these happy hours & specials on venue sites. Approve to launch them live on the Deals page.</p>
        </div>
        <button onClick={harvest} disabled={running} data-testid="harvest-deals-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white text-sm font-semibold px-4 py-2.5 flex items-center gap-2 disabled:opacity-50">
          {running ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} {running ? "Scanning…" : "Find Deals with Bot"}
        </button>
      </div>
      {status && status.state && status.state !== "idle" && (
        <div className="text-neutral-500 text-xs mb-4">Last run: {status.state} · {status.candidates || 0} found across {status.venues || 0} venues{status.scope && status.scope !== "all" ? ` (${status.scope})` : ""}.</div>
      )}
      {loading ? <div className="text-neutral-500 py-10 text-center">Loading…</div>
        : cands.length === 0 ? <div className="text-neutral-500 py-16 text-center border border-white/10">No pending deals. Click "Find Deals with Bot" to scan venue websites.</div>
        : Object.entries(groups).map(([venue, list]) => (
          <div key={venue} className="mb-6" data-testid={`deal-group-${venue}`}>
            <h3 className="font-accent text-[#00E5FF] text-sm mb-2">{venue} <span className="text-neutral-600">({list.length})</span></h3>
            <div className="space-y-2">{list.map((c) => <Candidate key={c.id} c={c} reload={load} />)}</div>
          </div>
        ))}
    </div>
  );
}
